import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RapportMissionService from "../../../services/RapportMissionService";
import LoadingTexteBackdrop from "../../tools/LoadingTexteBackdrop";
import TablePagination from "@mui/material/TablePagination";
import Grid from '@mui/material/Grid';
import CircularProgress from "@mui/material/CircularProgress";
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import UserService from "../../../services/UserService";
import PlanningMissionService from "../../../services/PlanningMissionService";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { LoadingButton } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import LabelIcon from '@mui/icons-material/Label';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Badge from '@mui/material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import { alpha } from '@mui/material/styles';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogTitle-root': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    cursor: 'pointer',
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  '&.MuiTableCell-body': {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    overflow: 'visible',
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'Valider' || status === 'validé' ? theme.palette.success.main : 
                   status === 'A modifier' ? theme.palette.warning.main : 
                   status === 'Refusé' ? theme.palette.error.main :
                   theme.palette.info.main,
  color: theme.palette.common.white,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: status === 'Valider' || status === 'validé' ? theme.palette.success.dark : 
                     status === 'A modifier' ? theme.palette.warning.dark : 
                     status === 'Refusé' ? theme.palette.error.dark :
                     theme.palette.info.dark,
  },
}));

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#e3f2fd',
  borderRadius: '8px',
  padding: '12px',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const TableWrapper = styled(Box)({
  overflowX: 'auto',
});

function createData(mission_planning_id, planning_destination, mission_title, reference, date_debut, date_fin, code, lieufinal, filename, statusrapportmission, filerapportmission, missionaire, activitymis) {
  return {
    mission_planning_id,
    planning_destination,
    mission_title,
    reference,
    date_debut,
    date_fin,
    code,
    lieufinal,
    filename,
    statusrapportmission,
    filerapportmission,
    missionaire,
    activitymis,
    history: {},
  };
}

function Row(props) {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState({});
  const lienpdf = process.env.REACT_APP_API_URL_FILE;
  const lienrapport = process.env.REACT_APP_API_URL_PDF_MIS;
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleRowClick = async () => {
    try {
      const result = await RapportMissionService.detailshistory(row.mission_planning_id, row.planning_destination);
      setHistory(result?.data || {});
      setDialogOpen(true);
    } catch (error) {
      setHistory({});
      setDialogOpen(true);
    }
  };

  const handleToggle = async (event) => {
    event.stopPropagation();
    if (!open) {
      setLoading(true);
      try {
        const result = await RapportMissionService.detailshistory(row.mission_planning_id, row.planning_destination);
        setHistory(result?.data || {});
      } catch (error) {
        setHistory({});
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  const renderList = (items, labelKey) => {
    if (!items || !Array.isArray(items) || items.length === 0) return 'Aucune donnée';
    return (
      <List dense disablePadding>
        {items.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
            <ListItemText 
              primary={item[labelKey] || item.description || item.libelle || item.username || `${item.nom} - ${item.function_code || ''}`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <React.Fragment>
      <StyledTableRow onClick={handleRowClick}>
        <TableCell sx={{ width: '40px', minWidth: '40px' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={handleToggle}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography variant="body2" fontWeight={500}>
            {row.mission_title}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color="textSecondary">
            {row.reference}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            <EventIcon fontSize="small" color="action" />
            <Typography variant="body2">{row.date_debut}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            <EventIcon fontSize="small" color="action" />
            <Typography variant="body2">{row.date_fin}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{row.code}</Typography>
        </TableCell>
        <TableCell align="right">
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            <LocationOnIcon fontSize="small" color="action" />
            <Typography variant="body2">{row.lieufinal}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          {row.missionaire && row.missionaire.length > 0 ? (
            <Box display="flex" flexWrap="wrap" justifyContent="flex-end">
              {row.missionaire.map((ts, index) => (
                <Chip
                  key={index}
                  icon={<PersonIcon />}
                  label={ts.username}
                  size="small"
                  sx={{ m: 0.3 }}
                />
              ))}
            </Box>
          ) : 'Aucun'}
        </TableCell>
        <TableCell align="right">
          {row.activitymis && row.activitymis.length > 0 ? (
            <Box display="flex" flexWrap="wrap" justifyContent="flex-end">
              {row.activitymis.map((ts, index) => (
                <Chip
                  key={index}
                  label={ts.libelle}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ m: 0.3 }}
                />
              ))}
            </Box>
          ) : 'Aucune'}
        </TableCell>
        <TableCell align="right">
          {row.filename ? (
            <Button
              component="a"
              href={lienpdf + row.filename + ".pdf"}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.7rem', padding: '2px 8px' }}
            >
              Voir
            </Button>
          ) : 'N/A'}
        </TableCell>
        <TableCell align="right">
          {row.filerapportmission ? (
            <Button
              component="a"
              href={`${lienrapport}/${row.filerapportmission}`}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="contained"
              color="primary"
              sx={{ fontSize: '0.7rem', padding: '2px 8px' }}
            >
              Voir
            </Button>
          ) : (
            <Typography variant="caption" color="textSecondary">
              Aucun
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <StatusChip
            label={row.statusrapportmission || 'En attente'}
            status={row.statusrapportmission}
            size="small"
          />
        </TableCell>
      </StyledTableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, padding: 2, backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.02), borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom component="div" color="primary">
                Détails de la mission
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          <PersonIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Missionnaires
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {renderList(history.missionaire, 'nom')}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          <DescriptionIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Contexte et justification
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="body2">
                          {history.contexte || 'Non spécifié'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          <LabelIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Objectifs
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {renderList(history.objectif, 'description')}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          <AssessmentIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Résultats attendus
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {renderList(history.resultatattendu, 'description')}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          Constats et Recommandations
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {history.constat && history.constat.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <StyledTableCell>Constat</StyledTableCell>
                                <StyledTableCell>Recommandations</StyledTableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {history.constat.map((ts, index) => (
                                <TableRow key={index}>
                                  <TableCell>{ts.description}</TableCell>
                                  <TableCell>
                                    {ts.recommandations && ts.recommandations.length > 0 ? (
                                      renderList(ts.recommandations, 'description')
                                    ) : 'Aucune recommandation'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="textSecondary" align="center">
                            Aucun constat disponible
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Dialog Modal */}
      <BootstrapDialog
        onClose={handleCloseDialog}
        aria-labelledby="customized-dialog-title"
        open={dialogOpen}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          <Box display="flex" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Détails de la mission - {row.reference}
            </Typography>
          </Box>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <PersonIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Missionnaires
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {renderList(history.missionaire, 'nom')}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <DescriptionIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Contexte et justification
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">{history.contexte || 'Non spécifié'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <LabelIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Objectifs
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {renderList(history.objectif, 'description')}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <AssessmentIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Résultats attendus
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {renderList(history.resultatattendu, 'description')}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Constats et Recommandations
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {history.constat && history.constat.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Constat</StyledTableCell>
                      <StyledTableCell>Recommandations</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.constat.map((ts, index) => (
                      <TableRow key={index}>
                        <TableCell>{ts.description}</TableCell>
                        <TableCell>
                          {ts.recommandations && ts.recommandations.length > 0 ? (
                            renderList(ts.recommandations, 'description')
                          ) : 'Aucune recommandation'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="textSecondary" align="center">
                  Aucun constat disponible
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Fermer
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    mission_planning_id: PropTypes.number.isRequired,
    planning_destination: PropTypes.number.isRequired,
    mission_title: PropTypes.string.isRequired,
    reference: PropTypes.string.isRequired,
    date_debut: PropTypes.string.isRequired,
    date_fin: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    lieufinal: PropTypes.string.isRequired,
    statusrapportmission: PropTypes.string,
    filerapportmission: PropTypes.string,
    missionaire: PropTypes.arrayOf(PropTypes.object),
    activitymis: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

export default function SuivirapportmissionTable() {
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [datatable, setDatatable] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [direction, setDirection] = useState([]);
  const [directiondest, setDirectiondest] = useState([]);
  const [service, setService] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [filter, setFilter] = useState({
    directionid: null,
    serviceid: null,
    status: null,
    date_debut: null,
    date_fin: null,
    directiondest: [],
    functionIds: [],
  });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [valdir, setValdir] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listefonction, setListefonction] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [defaultDirection, setDefaultDirection] = useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = datatable.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filter.date_debut) count++;
    if (filter.date_fin) count++;
    if (filter.directionid) count++;
    if (filter.serviceid) count++;
    if (filter.directiondest && filter.directiondest.length > 0) count++;
    if (filter.status) count++;
    if (filter.functionIds && filter.functionIds.length > 0) count++;
    return count;
  };

  const removeFilter = (filterName) => {
    switch (filterName) {
      case 'date_debut':
        setFilter({ ...filter, date_debut: '' });
        break;
      case 'date_fin':
        setFilter({ ...filter, date_fin: '' });
        break;
      case 'direction':
        setFilter({ ...filter, directionid: null });
        setSelectedDirection(null);
        setService([]);
        break;
      case 'service':
        setFilter({ ...filter, serviceid: null });
        setSelectedService(null);
        break;
      case 'directiondest':
        setFilter({ ...filter, directiondest: [] });
        setValdir([]);
        break;
      case 'status':
        setFilter({ ...filter, status: null });
        setSelectedStatus(null);
        break;
      case 'function':
        setFilter({ ...filter, functionIds: [] });
        setSelectedFunctions([]);
        break;
      default:
        break;
    }
  };

  const handleChangeDirdestination = (event, value) => {
    const safeValue = value || [];
    setValdir(safeValue);
    const ids = safeValue.map(item => item.direction_id);
    setFilter({ ...filter, directiondest: ids });
  };

  const statusOptions = [
    { id: 'En attente de validation', label: 'En attente de validation' },
    { id: 'Valider', label: 'Validé' },
    { id: 'A modifier', label: 'À modifier' }
  ];

  useEffect(() => {
    const userData = localStorage.getItem('USER');
    if (userData) {
      UserService.getUserSummaryByUserId(userData)
        .then((result) => {
          if (result?.data) {
            const dirpers = result.data.direction_id;
            PlanningMissionService.getDirection().then((result) => {
              if (result?.data) {
                let directions = result.data;
                if (parseInt(dirpers, 10) !== 1) {
                  directions = result.data.filter(ts => parseInt(ts.direction_id, 10) === parseInt(dirpers, 10));
                }
                setDirection(directions);
                setDirectiondest(directions);

                // Définir la direction par défaut pour les non-admins
                if (parseInt(dirpers, 10) !== 1 && directions.length > 0) {
                  const defaultDir = directions[0];
                  setDefaultDirection(defaultDir);
                  setSelectedDirection(defaultDir);
                  setFilter(prev => ({
                    ...prev,
                    directionid: defaultDir.direction_id,
                  }));

                  // Charger les services de la direction par défaut
                  PlanningMissionService.getServiceappui(defaultDir.direction_id)
                    .then((res) => {
                      if (res?.data) {
                        setService(res.data);
                      }
                    })
                    .catch((error) => {
                      console.error("Erreur lors du chargement du service par défaut :", error);
                    });
                }
              }
            }).catch((error) => {
              console.error('Error fetching direction data:', error);
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching user summary:', error);
        });
    }

    PlanningMissionService.listfunction().then((result) => {
      if (result?.data) {
        setListefonction(result.data);
      }
    }).catch((error) => {
      console.error('Error fetching fonction data:', error);
    });
  }, []);

  const handleChangeService = (event, value) => {
    if (!value) {
      setSelectedService(null);
      setFilter({ ...filter, serviceid: null });
      return;
    }
    setSelectedService(value);
    setFilter({ ...filter, serviceid: value.service_id });
  };

  const handleChangeStatus = (event, newValue) => {
    setSelectedStatus(newValue);
    setFilter({ ...filter, status: newValue?.id || null });
  };

  const handleChangeDirection = (event, value) => {
    if (!value) {
      setSelectedDirection(null);
      setFilter({ ...filter, directionid: null, serviceid: null });
      setSelectedService(null);
      setService([]);
      return;
    }

    setSelectedDirection(value);
    setFilter({ ...filter, directionid: value.direction_id });

    PlanningMissionService.getServiceappui(value.direction_id)
      .then((result) => {
        if (result?.data) {
          setService(result.data);
        }
      })
      .catch((error) => {
        console.error("Erreur lors du chargement du service :", error);
      });
  };

  const handleChangeFunction = (event, value) => {
    const safeValue = value || [];
    setSelectedFunctions(safeValue);
    const ids = safeValue.map(item => item.function_id);
    setFilter({ ...filter, functionIds: ids });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilter({ ...filter, [name]: value });
  };

  const handleFilter = () => {
    setOpenLoadingSimpleBackdrop(true);
    setIsLoading(true);
    RapportMissionService.rapportmissiondetails(
      filter.date_debut,
      filter.date_fin,
      filter.directiondest,
      filter.directionid,
      filter.serviceid,
      filter.status,
      filter.functionIds
    ).then((result) => {
      if (result?.data) {
        const formattedData = result.data.map(item => createData(
          item.mission_planning_id,
          item.planning_destination,
          item.mission_title,
          item.reference,
          item.date_debut,
          item.date_fin,
          item.code,
          item.lieufinal,
          item.filename,
          item.statusrapportmission,
          item.filerapportmission,
          item.missionaire,
          item.activitymis
        ));
        setDatatable(formattedData);
      } else {
        setDatatable([]);
      }
      setOpenLoadingSimpleBackdrop(false);
      setIsLoading(false);
    }).catch(() => {
      setDatatable([]);
      setOpenLoadingSimpleBackdrop(false);
      setIsLoading(false);
    });
  };

  const handleReinitialise = () => {
    setFilter({
      directionid: defaultDirection ? defaultDirection.direction_id : null,
      serviceid: null,
      status: null,
      date_debut: null,
      date_fin: null,
      directiondest: [],
      functionIds: [],
    });
    setSelectedDirection(defaultDirection || null);
    setSelectedService(null);
    setSelectedStatus(null);
    setSelectedFunctions([]);
    setValdir([]);
    setDatatable([]);

    // Recharger les services de la direction par défaut si disponible
    if (defaultDirection) {
      PlanningMissionService.getServiceappui(defaultDirection.direction_id)
        .then((res) => {
          if (res?.data) {
            setService(res.data);
          }
        })
        .catch((error) => {
          console.error("Erreur lors du rechargement du service :", error);
        });
    } else {
      setService([]);
    }
  };

  return (
    <>
      <LoadingTexteBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
      
      <Box style={{ padding: '24px', backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" style={{ fontWeight: 'bold', color: '#1a237e' }}>
              Suivi des rapports de mission
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              startIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              style={{ borderRadius: '20px', textTransform: 'none', position: 'relative' }}
            >
              {filtersExpanded ? "Masquer les filtres" : "Afficher les filtres"}
              {getActiveFiltersCount() > 0 && !filtersExpanded && (
                <Badge badgeContent={getActiveFiltersCount()} color="error" style={{ marginLeft: 8 }} />
              )}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReinitialise}
              startIcon={<RestartAltIcon />}
              style={{ borderRadius: '20px', textTransform: 'none' }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Box>

        <InfoBox>
          <InfoIcon style={{ color: '#1976d2' }} />
          <Typography variant="body2" style={{ color: '#1976d2' }}>
            Recherchez les rapports de mission par date, direction, service ou statut.
          </Typography>
        </InfoBox>

        {getActiveFiltersCount() > 0 && (
          <Box mb={2}>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 500 }}>
              Filtres actifs :
            </Typography>
            <Box mt={1} display="flex" flexWrap="wrap">
              {filter.date_debut && filter.date_fin && (
                <Chip
                  label={`Période: ${filter.date_debut} → ${filter.date_fin}`}
                  onDelete={() => { removeFilter('date_debut'); removeFilter('date_fin'); }}
                  size="small"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              )}
              {selectedDirection && (
                <Chip
                  label={`Direction missionnaire: ${selectedDirection.name}`}
                  onDelete={() => removeFilter('direction')}
                  size="small"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              )}
              {selectedService && (
                <Chip
                  label={`Service: ${selectedService.name}`}
                  onDelete={() => removeFilter('service')}
                  size="small"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              )}
              {valdir.length > 0 && (
                <Chip
                  label={`${valdir.length} direction(s) destination`}
                  onDelete={() => removeFilter('directiondest')}
                  size="small"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              )}
              {selectedStatus && (
                <Chip
                  label={`Statut: ${selectedStatus.label}`}
                  onDelete={() => removeFilter('status')}
                  size="small"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              )}
              {selectedFunctions.length > 0 && (
                <Chip
                  label={`${selectedFunctions.length} fonction(s)`}
                  onDelete={() => removeFilter('function')}
                  size="small"
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              )}
            </Box>
          </Box>
        )}

        <Collapse in={filtersExpanded}>
          <FilterCard>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date début"
                    size="small"
                    variant="outlined"
                    type="date"
                    value={filter.date_debut || ''}
                    name="date_debut"
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Date fin"
                    size="small"
                    variant="outlined"
                    type="date"
                    value={filter.date_fin || ''}
                    name="date_fin"
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    onChange={handleChangeDirection}
                    value={selectedDirection}
                    options={direction}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Direction des missionnaires"
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    onChange={handleChangeDirdestination}
                    multiple
                    value={valdir}
                    options={directiondest}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Direction de destination"
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    onChange={handleChangeFunction}
                    value={selectedFunctions}
                    options={listefonction}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fonction"
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Sélectionnez une ou plusieurs fonctions"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.function_id}
                          label={`${option.name} (${option.code})`}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    onChange={handleChangeService}
                    value={selectedService}
                    options={service}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Service des missionnaires"
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    onChange={handleChangeStatus}
                    value={selectedStatus}
                    options={statusOptions}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Statut"
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleFilter}
                    loading={isLoading}
                    fullWidth
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: '20px',
                      textTransform: 'none',
                    }}
                    startIcon={<SearchIcon />}
                  >
                    Rechercher
                  </LoadingButton>
                </Grid>
              </Grid>
            </CardContent>
          </FilterCard>
        </Collapse>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
                <Typography variant="h6" color="primary">
                  Liste des rapports de mission
                  {datatable.length > 0 && (
                    <Chip
                      label={`${datatable.length} résultat${datatable.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Typography>
              </Box>
              <TableWrapper>
                <TableContainer>
                  <Table sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell sx={{ width: '40px', minWidth: '40px' }} />
                        <StyledTableCell>Objet de la mission</StyledTableCell>
                        <StyledTableCell align="right">Référence</StyledTableCell>
                        <StyledTableCell align="right">Date début</StyledTableCell>
                        <StyledTableCell align="right">Date fin</StyledTableCell>
                        <StyledTableCell align="right">Direction dest.</StyledTableCell>
                        <StyledTableCell align="right">Lieu</StyledTableCell>
                        <StyledTableCell align="right">Missionnaires</StyledTableCell>
                        <StyledTableCell align="right">Activités MIS</StyledTableCell>
                        <StyledTableCell align="right">TDR</StyledTableCell>
                        <StyledTableCell align="right">Rapport</StyledTableCell>
                        <StyledTableCell align="right">Statut</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleRows.length > 0 ? (
                        visibleRows.map((row) => (
                          <Row key={row.mission_planning_id} row={row} />
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                            <Typography variant="body1" color="textSecondary">
                              Aucune donnée disponible
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableWrapper>
              <TablePagination
                component="div"
                count={datatable.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Lignes par page"
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}